import gsap from 'gsap';
import InertiaPlugin from 'gsap/dist/InertiaPlugin';
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import useUnmount from '@/hooks/useUnmount';

/**
 * Registering the Draggable plugin when there is no Window will throw errors,
 * therefore we create this little proxy method to import draggable dynamically,
 */
export const getDraggable = async (): Promise<typeof Draggable | null> => {
  if (typeof window === 'undefined') return null;

  const { Draggable } = await import('gsap/Draggable');
  gsap.registerPlugin(Draggable, InertiaPlugin);

  return Draggable;
};

/**
 * Hook to easily use draggable in your components
 * @param target
 * @param vars
 * @param options
 */
export const useDraggable = (
  target: RefObject<HTMLElement | null>,
  vars?: Draggable.Vars,
  options?: {
    bounds?: RefObject<HTMLDivElement | null>;
    trigger?: RefObject<HTMLDivElement | null>;
  },
): Draggable | null => {
  const draggableInstance = useRef<Draggable | null>(null);
  const varsRef = useRef(vars);

  useEffect(() => {
    (async () => {
      const Draggable = await getDraggable();

      if (Draggable && target.current) {
        [draggableInstance.current] = Draggable.create(target.current, {
          bounds: options?.bounds?.current,
          trigger: options?.trigger?.current,
          ...varsRef.current,
        });
      }
    })();
  }, [target, varsRef, options?.trigger, options?.bounds]);

  useUnmount(() => draggableInstance.current?.kill());

  return draggableInstance.current;
};
